import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import UpgradeModal from "@/components/UpgradeModal";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building, Upload, X, Download, Trash2, Lock } from "lucide-react";
import SubscriptionManagement from "@/components/SubscriptionManagement";

export default function Settings() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    owner_name: "",
    address: "",
    tax_id: "",
    phone: "",
    email: "",
    website: "",
    bank_name: "",
    iban: "",
    bic: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setFormData({
        company_name: response.data.company_name || "",
        owner_name: response.data.owner_name || "",
        address: response.data.address || "",
        tax_id: response.data.tax_id || "",
        phone: response.data.phone || "",
        email: response.data.email || "",
        website: response.data.website || "",
        bank_name: response.data.bank_name || "",
        iban: response.data.iban || "",
        bic: response.data.bic || "",
      });
      if (response.data.logo_url) {
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload logo if changed
      if (logoFile) {
        const formDataLogo = new FormData();
        formDataLogo.append("file", logoFile);
        await api.post(`/settings/upload-logo`, formDataLogo, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Update settings
      await api.put(`/settings`, formData);
      toast.success(t("settings.saved"));
      fetchSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async () => {
    try {
      const response = await api.get('/user/export-data');
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ivoliq-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('settings.dataExported'));
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('settings.exportError'));
    }
  };

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('settings.fillAllPasswordFields', 'Bitte füllen Sie alle Felder aus'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.passwordsDontMatch', 'Neue Passwörter stimmen nicht überein'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.passwordTooShort', 'Neues Passwort muss mindestens 8 Zeichen haben'));
      return;
    }

    try {
      await api.post('/auth/change-password', null, {
        params: {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        }
      });
      toast.success(t('settings.passwordChanged', 'Passwort erfolgreich geändert'));
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      // Logout and redirect to login after password change
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.currentPasswordIncorrect', 'Aktuelles Passwort ist falsch'));
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('OAuth')) {
        toast.error(t('settings.cannotChangeOAuthPassword', 'Passwort kann für OAuth-Accounts nicht geändert werden'));
      } else {
        toast.error(t('settings.passwordChangeError', 'Fehler beim Ändern des Passworts'));
      }
    }
  };

  // Delete Account State
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error(t('settings.passwordRequired', 'Passwort erforderlich'));
      return;
    }

    try {
      await api.post('/auth/delete-account', { password: deletePassword });
      toast.success(t('settings.accountDeleted', 'Account wurde erfolgreich gelöscht'));
      setDeleteDialogOpen(false);
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.invalidPassword', 'Falsches Passwort'));
      } else {
        toast.error(t('settings.deleteError', 'Fehler beim Löschen des Accounts'));
      }
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("settings.title")}</h1>
        <p className="text-lg text-gray-600">{t("settings.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.logo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="h-24 w-24 object-contain border-2 border-gray-200 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Building className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span>{t("settings.uploadLogo")}</span>
                  </div>
                </Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <p className="text-xs text-gray-500 mt-2">
                  PNG, JPG oder SVG (empfohlen: 200x200px)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.companyInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.companyName")}</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Meine Firma GmbH"
                />
              </div>
              <div>
                <Label>{t("settings.ownerName")}</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="Max Mustermann"
                />
              </div>
            </div>

            <div>
              <Label>{t("settings.address")}</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Musterstraße 123&#10;12345 Berlin"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("settings.taxId")}</Label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  placeholder="DE123456789"
                />
              </div>
              <div>
                <Label>{t("settings.phone")}</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <Label>{t("settings.email")}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="kontakt@firma.de"
                />
              </div>
            </div>

            <div>
              <Label>{t("settings.website")}</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="www.meinefirma.de"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bank Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.bankInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("settings.bankName")}</Label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Beispielbank"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.iban")}</Label>
                <Input
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
              </div>
              <div>
                <Label>{t("settings.bic")}</Label>
                <Input
                  value={formData.bic}
                  onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                  placeholder="COBADEFFXXX"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              {t("settings.exportDataTitle")}
            </CardTitle>
            <CardDescription>{t("settings.exportDataDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleDataExport}
              className="w-full"
            >
              {t("settings.exportData")}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-600">
              <Lock className="h-5 w-5" />
              {t("settings.changePasswordTitle", "Passwort ändern")}
            </CardTitle>
            <CardDescription>
              {t("settings.changePasswordDesc", "Ändern Sie Ihr Login-Passwort. Sie werden nach der Änderung abgemeldet.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                >
                  {t("settings.changePassword", "Passwort ändern")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("settings.changePasswordTitle", "Passwort ändern")}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {t("settings.changePasswordInfo", "Nach der Änderung werden Sie abgemeldet und müssen sich mit dem neuen Passwort anmelden.")}
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="current-password" className="text-sm font-medium">
                          {t("settings.currentPassword", "Aktuelles Passwort")}
                        </Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          placeholder={t("settings.enterCurrentPassword", "Aktuelles Passwort eingeben")}
                          className="mt-1"
                          autoComplete="current-password"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-password" className="text-sm font-medium">
                          {t("settings.newPassword", "Neues Passwort")}
                        </Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          placeholder={t("settings.enterNewPassword", "Neues Passwort (min. 8 Zeichen)")}
                          className="mt-1"
                          autoComplete="new-password"
                          minLength={8}
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm-password" className="text-sm font-medium">
                          {t("settings.confirmNewPassword", "Neues Passwort bestätigen")}
                        </Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                          placeholder={t("settings.confirmNewPassword", "Neues Passwort bestätigen")}
                          className="mt-1"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })}>
                    {t("common.cancel", "Abbrechen")}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleChangePassword}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  >
                    {t("settings.changePassword", "Passwort ändern")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t("settings.deleteAccountTitle", "Account löschen")}
            </CardTitle>
            <CardDescription>
              {t("settings.deleteAccountDesc", "Löschen Sie Ihren Account und alle zugehörigen Daten unwiderruflich.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button 
                  type="button" 
                  variant="destructive" 
                  className="w-full"
                >
                  {t("settings.deleteAccount", "Account löschen")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("settings.confirmDeleteTitle", "Account wirklich löschen?")}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                      {t("settings.confirmDeleteDesc", "Diese Aktion kann nicht rückgängig gemacht werden. Folgende Daten werden gelöscht:")}
                    </p>
                    <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                      <li>{t("settings.deleteData1", "Alle Projekte und Zeiteinträge")}</li>
                      <li>{t("settings.deleteData2", "Alle Kunden und Rechnungen")}</li>
                      <li>{t("settings.deleteData3", "Alle Einnahmen und Ausgaben")}</li>
                      <li>{t("settings.deleteData4", "Ihr Stripe-Abonnement wird gekündigt")}</li>
                    </ul>
                    <div className="pt-3">
                      <Label htmlFor="delete-password" className="text-sm font-medium">
                        {t("settings.confirmPassword", "Passwort zur Bestätigung")}
                      </Label>
                      <Input
                        id="delete-password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder={t("settings.enterPassword", "Passwort eingeben")}
                        className="mt-2"
                        autoComplete="current-password"
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeletePassword("")}>
                    {t("common.cancel", "Abbrechen")}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteAccount}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={!deletePassword}
                  >
                    {t("settings.confirmDelete", "Endgültig löschen")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        <Button type="submit" className="w-full" disabled={loading} data-testid="save-settings-button">
          {loading ? "Speichert..." : t("common.save")}
        </Button>
      </form>

      {/* Subscription Management - outside form */}
      <SubscriptionManagement />

      {/* Country Information */}
      <CountryInfo />
    </div>
  );
}

// New component for Country Information with edit capability
function CountryInfo() {
  const { t } = useTranslation();
  const [countryInfo, setCountryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCanton, setSelectedCanton] = useState('');
  const [saving, setSaving] = useState(false);

  const countries = [
    { code: 'DE', name: t('country.germany', 'Deutschland'), flag: '🇩🇪' },
    { code: 'AT', name: t('country.austria', 'Österreich'), flag: '🇦🇹' },
    { code: 'CH', name: t('country.switzerland', 'Schweiz'), flag: '🇨🇭' },
    { code: 'OTHER', name: t('country.other', 'Anderes Land'), flag: '🌍' }
  ];

  useEffect(() => {
    const fetchCountryInfo = async () => {
      try {
        const response = await api.get('/country/info');
        setCountryInfo(response.data);
        setSelectedCountry(response.data.country_code);
      } catch (error) {
        console.error("Error fetching country info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountryInfo();
  }, []);

  const handleSave = async () => {
    // Validation for CH: canton must be selected
    if (selectedCountry === 'CH' && !selectedCanton) {
      toast.error(t('settings.cantonRequired', 'Bitte wählen Sie einen Kanton aus'));
      return;
    }
    
    setSaving(true);
    try {
      const params = { country_code: selectedCountry };
      if (selectedCountry === 'CH' && selectedCanton) {
        params.canton = selectedCanton;
      }
      
      await api.put('/country/update', null, { params });
      
      // Refresh country info
      const response = await api.get('/country/info');
      setCountryInfo(response.data);
      setEditing(false);
      toast.success(t('settings.countryUpdated', 'Steueransässigkeit aktualisiert'));
    } catch (error) {
      console.error("Error updating country:", error);
      toast.error(t('settings.countryUpdateError', 'Fehler beim Aktualisieren'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedCountry(countryInfo.country_code);
    setSelectedCanton(countryInfo.canton || '');
    setEditing(false);
  };

  if (loading) {
    return null;
  }

  if (!countryInfo) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{t("settings.countryInfo")}</CardTitle>
            <CardDescription>{t("settings.countryInfoDesc")}</CardDescription>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t("settings.editCountry", "Ändern")}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {editing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("settings.selectTaxCountry", "Land der Steuerpflicht")}
                </label>
                <div className="space-y-2">
                  {countries.map(country => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => setSelectedCountry(country.code)}
                      className={`w-full flex items-center justify-between p-3 border-2 rounded-lg transition-all ${
                        selectedCountry === country.code
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{country.flag}</span>
                        <span className="font-medium text-gray-900">{country.name}</span>
                      </div>
                      {selectedCountry === country.code && (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Canton selection for Switzerland */}
              {selectedCountry === 'CH' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("settings.selectCanton", "Wählen Sie Ihren Kanton")}
                  </label>
                  <select
                    value={selectedCanton || ''}
                    onChange={(e) => setSelectedCanton(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t("settings.selectCantonPlaceholder", "Bitte wählen...")}</option>
                    <option value="ZH">🇨🇭 Zürich</option>
                    <option value="ZG">🇨🇭 Zug (Niedrigste Steuern)</option>
                    <option value="BS">🇨🇭 Basel-Stadt</option>
                    <option value="BL">🇨🇭 Basel-Landschaft</option>
                    <option value="BE">🇨🇭 Bern</option>
                    <option value="GE">🇨🇭 Genf (Höchste Steuern)</option>
                    <option value="VD">🇨🇭 Waadt</option>
                    <option value="LU">🇨🇭 Luzern</option>
                    <option value="SG">🇨🇭 St. Gallen</option>
                    <option value="AG">🇨🇭 Aargau</option>
                    <option value="TI">🇨🇭 Tessin</option>
                    <option value="GR">🇨🇭 Graubünden</option>
                  </select>
                  <p className="text-xs text-gray-600 mt-2">
                    {t("settings.cantonNote", "Ihr Kanton wird für die Steuerberechnung benötigt. Die Steuersätze variieren stark zwischen den Kantonen.")}
                  </p>
                </div>
              )}
              
              {/* Warning for OTHER country selection */}
              {selectedCountry === 'OTHER' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryTitle", "Eingeschränkte Funktionalität")}
                      </p>
                      <p className="mb-2">
                        {t("settings.otherCountryWarning", "Für Ihr Land sind keine spezifischen Steuerberechnungen verfügbar.")}
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{t("settings.otherCountryLimit1", "Keine automatische Steuervorberechnung")}</li>
                        <li>{t("settings.otherCountryLimit2", "Generische Mehrwertsteuersätze")}</li>
                        <li>{t("settings.otherCountryLimit3", "Sie müssen Steuersätze manuell in Rechnungen eingeben")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || selectedCountry === countryInfo.country_code}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {saving ? t("settings.saving", "Speichern...") : t("settings.save", "Speichern")}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {t("settings.cancel", "Abbrechen")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.country")}:</span>
                <span className="text-gray-700">{countryInfo.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.currency")}:</span>
                <span className="text-gray-700">{countryInfo.currency}</span>
              </div>
              <div>
                <span className="font-medium">{t("settings.vatRates")}:</span>
                <ul className="mt-2 space-y-1 ml-4">
                  {countryInfo.vat_rates?.map((rate, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      {rate.label}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Info box for OTHER country */}
              {countryInfo.country_code === 'OTHER' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryInfoTitle", "Hinweis zur Steuerberechnung")}
                      </p>
                      <p>
                        {t("settings.otherCountryInfoText", "Für Ihr Land sind keine automatischen Steuerberechnungen verfügbar. Sie können die generischen Mehrwertsteuersätze verwenden oder eigene Werte in Ihren Rechnungen eintragen.")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-4">
                {t("settings.countryNote")}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
